import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import axios from 'axios';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { AlertTriangle, MapPin, Anchor, User, Check, Send, Loader2, ChevronDown, Waves } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const ITEM_TYPES = [
  { value: 'reti_fantasma', label: 'Reti fantasma', icon: '🕸️' },
  { value: 'pneumatici', label: 'Pneumatici', icon: '🛞' },
  { value: 'ancore', label: 'Ancore/Catene', icon: '⚓' },
  { value: 'plastica', label: 'Rifiuti plastici', icon: '🥤' },
  { value: 'metallo', label: 'Rottami metallici', icon: '🔩' },
  { value: 'altro', label: 'Altro', icon: '❓' }
];

const DEPTHS = [
  { value: '0-5m', label: '0-5 metri' },
  { value: '5-10m', label: '5-10 metri' },
  { value: '10-20m', label: '10-20 metri' },
  { value: '20m+', label: 'Oltre 20 metri' }
];

const DIFFICULTIES = [
  { value: 'facile', label: 'Facile', color: 'text-green-400' },
  { value: 'medio', label: 'Media', color: 'text-yellow-400' },
  { value: 'difficile', label: 'Difficile', color: 'text-red-400' }
];

// Location Picker Component
const LocationPicker = ({ value, onChange }) => {
  const defaultCenter = { lat: 45.4371, lng: 10.9927 }; // Verona
  const [markerPosition, setMarkerPosition] = useState(value || defaultCenter);

  const handleMapClick = (e) => {
    if (e.detail?.latLng) {
      const newPos = {
        lat: e.detail.latLng.lat,
        lng: e.detail.latLng.lng
      };
      setMarkerPosition(newPos);
      onChange(newPos);
    }
  };

  return (
    <div className="h-48 rounded-xl overflow-hidden border border-slate-600">
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          defaultCenter={markerPosition}
          defaultZoom={12}
          mapId="underwater-map"
          onClick={handleMapClick}
          gestureHandling="greedy"
          disableDefaultUI={true}
          style={{ width: '100%', height: '100%' }}
        >
          {markerPosition && (
            <AdvancedMarker position={markerPosition}>
              <div className="text-3xl">🤿</div>
            </AdvancedMarker>
          )}
        </Map>
      </APIProvider>
      <p className="text-xs text-slate-500 text-center mt-1">
        Tocca la mappa per segnare il punto esatto
      </p>
    </div>
  );
};

const SubacqueiSection = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  
  // Form state
  const [description, setDescription] = useState('');
  const [itemType, setItemType] = useState('');
  const [depth, setDepth] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [notes, setNotes] = useState('');
  const [location, setLocation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchReports = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/underwater-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleSubmit = async () => {
    if (!itemType) {
      toast.error('Seleziona il tipo di ritrovamento');
      return;
    }
    
    if (!description.trim()) {
      toast.error('Inserisci una descrizione');
      return;
    }
    
    if (!location) {
      toast.error('Seleziona la posizione sulla mappa');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/underwater-reports`,
        {
          location,
          description: description.trim(),
          item_type: itemType,
          depth,
          difficulty,
          notes: notes.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Segnalazione subacquea inviata!');
      setShowForm(false);
      resetForm();
      fetchReports();
    } catch (error) {
      console.error('Errore:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Errore invio segnalazione');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setDescription('');
    setItemType('');
    setDepth('');
    setDifficulty('');
    setNotes('');
    setLocation(null);
  };

  const takeReport = async (reportId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/underwater-reports/${reportId}/take`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Hai preso in carico la segnalazione!');
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const resolveReport = async (reportId) => {
    if (!window.confirm('Confermi di aver recuperato il materiale?')) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/underwater-reports/${reportId}/resolve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Segnalazione completata!');
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const getItemTypeInfo = (type) => {
    return ITEM_TYPES.find(t => t.value === type) || { label: type, icon: '❓' };
  };

  const activeReports = reports.filter(r => r.status === 'active');
  const takenReports = reports.filter(r => r.status === 'taken');

  return (
    <div className="max-w-4xl mx-auto p-6 pb-24">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="w-20 h-20 mx-auto bg-blue-500/20 rounded-full flex items-center justify-center mb-4">
          <span className="text-4xl">🤿</span>
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
          <TranslatedText text="SEGNALAZIONI SUB" />
        </h2>
        <p className="text-slate-400 mt-2"><TranslatedText text="Segnala ritrovamenti subacquei alla community" /></p>
      </div>

      {/* New Report Button */}
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="w-full mb-6 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold text-lg rounded-xl transition-all flex items-center justify-center gap-3 shadow-lg shadow-blue-500/25"
        >
          <Waves className="w-6 h-6" />
          <TranslatedText text="NUOVA SEGNALAZIONE SUBACQUEA" />
        </button>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-slate-900/80 backdrop-blur-xl border border-blue-500/30 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-blue-400" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              <TranslatedText text="Nuova Segnalazione" />
            </h3>
            <button
              onClick={() => { setShowForm(false); resetForm(); }}
              className="text-slate-400 hover:text-white"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            {/* Item Type */}
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">
                Tipo di ritrovamento *
              </label>
              <div className="grid grid-cols-2 gap-2">
                {ITEM_TYPES.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setItemType(type.value)}
                    className={`p-3 rounded-xl border text-left flex items-center gap-2 transition-all ${
                      itemType === type.value
                        ? 'bg-blue-500/20 border-blue-500 text-blue-400'
                        : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-600'
                    }`}
                  >
                    <span className="text-xl">{type.icon}</span>
                    <span className="text-sm">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">
                Descrizione *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descrivi cosa hai visto sott'acqua..."
                className="w-full h-24 bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none resize-none"
              />
            </div>

            {/* Location */}
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">
                Posizione sulla mappa *
              </label>
              <LocationPicker value={location} onChange={setLocation} />
            </div>

            {/* Depth */}
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">
                Profondità approssimativa
              </label>
              <div className="grid grid-cols-4 gap-2">
                {DEPTHS.map(d => (
                  <button
                    key={d.value}
                    onClick={() => setDepth(d.value)}
                    className={`p-2 rounded-lg border text-center text-sm transition-all ${
                      depth === d.value
                        ? 'bg-blue-500/20 border-blue-500 text-blue-400'
                        : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-600'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Difficulty */}
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">
                Difficoltà di recupero
              </label>
              <div className="grid grid-cols-3 gap-2">
                {DIFFICULTIES.map(d => (
                  <button
                    key={d.value}
                    onClick={() => setDifficulty(d.value)}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      difficulty === d.value
                        ? 'bg-blue-500/20 border-blue-500'
                        : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                    } ${d.color}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">
                Note aggiuntive
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="es: visibilità scarsa, corrente forte..."
                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full py-4 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Invio in corso...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Invia Segnalazione
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-blue-400">{activeReports.length}</p>
          <p className="text-slate-400 text-sm">Da recuperare</p>
        </div>
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-orange-400">{takenReports.length}</p>
          <p className="text-slate-400 text-sm">In carico</p>
        </div>
      </div>

      {/* Reports List */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-white">Segnalazioni Attive</h3>
        
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-10 bg-slate-900/50 rounded-xl border border-slate-700">
            <Waves className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">Nessuna segnalazione subacquea</p>
            <p className="text-slate-500 text-sm mt-1">Sii il primo a segnalare un ritrovamento!</p>
          </div>
        ) : (
          reports.map(report => {
            const typeInfo = getItemTypeInfo(report.item_type);
            const isActive = report.status === 'active';
            const isTaken = report.status === 'taken';
            
            return (
              <div 
                key={report.id}
                className={`bg-slate-900/60 backdrop-blur-xl border rounded-xl p-4 ${
                  isActive ? 'border-blue-500/30' : 'border-orange-500/30'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
                    isActive ? 'bg-blue-500/20' : 'bg-orange-500/20'
                  }`}>
                    {typeInfo.icon}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                        isActive ? 'bg-blue-500/20 text-blue-400' : 'bg-orange-500/20 text-orange-400'
                      }`}>
                        {typeInfo.label}
                      </span>
                      {report.depth && (
                        <span className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded">
                          {report.depth}
                        </span>
                      )}
                      {report.difficulty && (
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          report.difficulty === 'facile' ? 'bg-green-500/20 text-green-400' :
                          report.difficulty === 'medio' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {report.difficulty}
                        </span>
                      )}
                    </div>
                    
                    <p className="text-white text-sm mb-2">{report.description}</p>
                    
                    {report.notes && (
                      <p className="text-slate-400 text-xs mb-2">📝 {report.notes}</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span>🤿 {report.reporter_name}</span>
                      <span>📍 {report.location?.lat?.toFixed(4)}, {report.location?.lng?.toFixed(4)}</span>
                    </div>
                    
                    {isTaken && report.taken_by_name && (
                      <p className="text-orange-400 text-xs mt-2">
                        In carico a: {report.taken_by_name}
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-4">
                  {isActive && (
                    <button
                      onClick={() => takeReport(report.id)}
                      className="flex-1 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2"
                    >
                      <Anchor className="w-4 h-4" />
                      Ci penso io
                    </button>
                  )}
                  {isTaken && (
                    <button
                      onClick={() => resolveReport(report.id)}
                      className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      Recuperato
                    </button>
                  )}
                  <a
                    href={`https://www.google.com/maps?q=${report.location?.lat},${report.location?.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                  >
                    <MapPin className="w-4 h-4" />
                    Mappa
                  </a>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SubacqueiSection;
